import React, { useRef, useEffect, useState } from "react";  
import jsQR from "jsqr";  
import {  
    Button,  
    Dialog,  
    DialogActions,  
    DialogBody,  
    DialogContent,  
    DialogSurface,  
    DialogTitle,  
    DialogTrigger,  
    Spinner,  
} from "@fluentui/react-components";  
import { Dismiss20Regular } from "@fluentui/react-icons";  
  
type QrScannerProps = {  
    onScan: (result: string) => void;  
    onClose: () => void;  
    getResponderId?: (responderId: string) => void;  
    dialogTitle?: string;  
};  
  
const getResponderIdFromQrUrl = (url: string): string | null => {  
    try {  
        const u = new URL(url);  
        return u.searchParams.get("responderId");  
    } catch {  
        return null;  
    }  
};  
  
const QrScanner: React.FC<QrScannerProps> = ({  
    onScan,  
    onClose,  
    getResponderId,  
    dialogTitle = "Scan QR code"  
}) => {  
    const [open, setOpen] = useState(true);  
    const videoRef = useRef<HTMLVideoElement | null>(null);  
    const canvasRef = useRef<HTMLCanvasElement | null>(null);  
    const scanIntervalRef = useRef<number>();  
    const streamRef = useRef<MediaStream | null>(null);  
    const [scanError, setScanError] = useState<string | null>(null);  
  
    const stopCamera = () => {  
        if (streamRef.current) {  
            streamRef.current.getTracks().forEach((track) => {  
                try {  
                    track.stop();  
                } catch {}  
            });  
            streamRef.current = null;  
        }  
        if (videoRef.current) {  
            try {  
                videoRef.current.pause();  
            } catch {}  
            videoRef.current.srcObject = null;  
        }  
    };  
  
    const killAllMediaTracks = async () => {  
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;  
        const devices = await navigator.mediaDevices.enumerateDevices();  
        devices  
            .filter(dev => dev.kind === "videoinput")  
            .forEach(() => {});  
    };  
  
    useEffect(() => {  
        setScanError(null);  
  
        if (!open) {  
            stopCamera();  
            if (scanIntervalRef.current) {  
                window.clearInterval(scanIntervalRef.current);  
            }  
            killAllMediaTracks();  
            return;  
        }  
  
        navigator.mediaDevices  
            .getUserMedia({ video: { facingMode: "environment" } })  
            .then((stream) => {  
                stopCamera();  
  
                streamRef.current = stream;  
                if (videoRef.current) {  
                    videoRef.current.srcObject = stream;  
                    videoRef.current.setAttribute("playsinline", "true");  
                    videoRef.current.play();  
                }  
  
                scanIntervalRef.current = window.setInterval(() => {  
                    if (!videoRef.current || !canvasRef.current) return;  
                    const video = videoRef.current;  
                    const canvas = canvasRef.current;  
                    const ctx = canvas.getContext("2d");  
                    if (!ctx) return;  
                    if (!video.videoWidth || !video.videoHeight) return;  
  
                    canvas.width = video.videoWidth;  
                    canvas.height = video.videoHeight;  
  
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);  
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);  
  
                    const code = jsQR(imageData.data, imageData.width, imageData.height);  
  
                    if (code && code.data) {  
                        stopCamera();  
  
                        if (scanIntervalRef.current) {  
                            window.clearInterval(scanIntervalRef.current);  
                        }  
  
                        if (getResponderId) {  
                            const responderId = getResponderIdFromQrUrl(code.data);  
                            if (responderId) {  
                                setOpen(false);  
                                getResponderId(responderId);  
                                return;  
                            }  
                        }  
                        setOpen(false);  
                        onScan(code.data);  
                    }  
                }, 250);  
            })  
            .catch((err) => {  
                setScanError("Unable to access camera: " + err.message);  
            });  
  
        return () => {  
            if (scanIntervalRef.current) {  
                window.clearInterval(scanIntervalRef.current);  
            }  
            stopCamera();  
            killAllMediaTracks();  
        };  
    }, [open, onScan, getResponderId]);  
  
    const handleClose = () => {  
        setOpen(false);  
        onClose();  
    };  
  
    return (  
        <Dialog open={open} onOpenChange={(_, data) => {  
            if (!data.open) {  
                handleClose();  
            }  
        }}>  
            <DialogSurface>  
                <DialogBody>  
                    <DialogTitle action={  
                        <DialogTrigger action="close">  
                            <Button appearance="subtle" icon={<Dismiss20Regular />} />  
                        </DialogTrigger>  
                    }>  
                        {dialogTitle}  
                    </DialogTitle>  
                    <DialogContent>  
                        <div className="qr-scanner-modal">  
                            <div className="qr-scanner-content">  
                                <video ref={videoRef} style={{ width: 400, maxWidth: "100%" }} muted autoPlay playsInline />  
                                <canvas ref={canvasRef} style={{ display: "none" }} />  
                                {scanError && <div className="scan-error">{scanError}</div>}  
                            </div>  
                        </div>  
                    </DialogContent>  
                    <DialogActions>  
                        <Button appearance="secondary" onClick={handleClose}>  
                            Close
                        </Button>  
                    </DialogActions>  
                </DialogBody>  
            </DialogSurface>  
        </Dialog>  
    );  
};  
  
export default QrScanner;  